import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Warehouse } from 'lucide-react'
import './PropertyDetail.css'

import { useStore } from '@/store/useStore'
import { usePropertyFinancials } from '@/hooks/usePropertyFinancials'
import {
  PageHeader, EmptyState, Button, Badge, HealthBadge, Tabs, QuickActionBar, toActionContacts, Delta,
} from '@/components/ui'
import type { TabDef } from '@/components/ui'
import { RecordHero } from '@/components/workspace/RecordHero'
import type { HeroKpi } from '@/components/workspace/RecordHero'

import {
  propertyById, accountById, fundById, latest, contactsForAccount,
  workOrdersForProperty, actionItemsForProperty, activitiesForRef, financialPeriodsForProperty,
} from '@/lib/selectors'
import { money, num } from '@/lib/format'

import { OverviewTab } from './OverviewTab'
import { InsightsTab } from './InsightsTab'
import {
  FinancialsTab, VitalFactorsTab, WorkOrdersTab, ActionItemsTab, HistoryTab, FilesTab,
} from './DetailTabs'
import { propertyFinancialPeriod, delinquencyRead, trendDelta } from './propertyMetrics'

type TabId =
  | 'overview' | 'insights' | 'financials' | 'vital'
  | 'workorders' | 'actions' | 'history' | 'files'

export default function PropertyDetail() {
  const { id } = useParams()
  const d = useStore((s) => s.data)
  const [tab, setTab] = useState<TabId>('overview')
  // P&L arrives lazily from the API (crm.financials.view-gated) and lands in
  // the store, where the selectors below pick it up on the re-render.
  usePropertyFinancials(id)

  const p = propertyById(d, id)
  if (!p) {
    return (
      <div className="pd-wrap">
        <PageHeader
          eyebrow="Property"
          title="Property not found"
          breadcrumb={<Link to="/properties">Properties</Link>}
        />
        <EmptyState
          icon={<Warehouse size={40} />}
          title="No such asset"
          hint={<>We couldn't find a property with id <b>{id}</b>.</>}
          action={<Link className="pd-link" to="/properties">Back to Properties</Link>}
        />
      </div>
    )
  }

  const owner = accountById(d, p.ownerAccountId)
  const fund = fundById(d, p.fundId)
  const fp = propertyFinancialPeriod(d, p)

  // hero KPIs
  const occ = latest(p.occupancyPct)
  const occDelta = trendDelta(p.occupancyPct)
  const revLatest = latest(p.revenue)
  const del = delinquencyRead(fp)

  const kpis: HeroKpi[] = [
    {
      label: 'Occupancy',
      value: occ != null ? occ.toFixed(1) : '—',
      unit: occ != null ? '%' : undefined,
      delta: occDelta != null
        ? <Delta value={occDelta} higherIsBetter>{`${occDelta >= 0 ? '+' : ''}${occDelta.toFixed(1)} pts`}</Delta>
        : undefined,
    },
    {
      label: 'In-Place Revenue',
      value: revLatest != null ? money(revLatest, { compact: true }) : '—',
      unit: revLatest != null ? '/mo' : undefined,
    },
    { label: 'NRSF', value: p.nrsf != null ? num(p.nrsf) : '—' },
    { label: 'Units', value: p.totalUnits != null ? num(p.totalUnits) : '—' },
  ]
  if (del.pct != null) {
    kpis.push({ label: 'Delinquency', value: del.pct.toFixed(1), unit: '%' })
  }

  // counts for tab badges
  const woCount = workOrdersForProperty(d, p.id).length
  const aiCount = actionItemsForProperty(d, p.id).length
  const histCount = activitiesForRef(d, 'property', p.id).length
  const fpCount = financialPeriodsForProperty(d, p.id).length
  const fileCount = d.files.filter((f) => f.attachedTo.kind === 'property' && f.attachedTo.id === p.id).length

  const tabs: TabDef[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'insights', label: 'Insights' },
    { id: 'financials', label: 'Financials', count: fpCount || undefined },
    { id: 'vital', label: 'Vital Factors' },
    { id: 'workorders', label: 'Work Orders', count: woCount || undefined },
    { id: 'actions', label: 'Action Items', count: aiCount || undefined },
    { id: 'history', label: 'History', count: histCount || undefined },
    { id: 'files', label: 'Files', count: fileCount || undefined },
  ]

  const eyebrow = [p.relationshipType, p.brand, p.externalIds?.lcode].filter(Boolean).join(' · ')

  const badges = (
    <>
      {p.address?.state && <Badge tone="navy" variant="soft">{p.address.state}</Badge>}
      {p.stabilization && (
        <Badge tone={p.stabilization === 'Stabilized' ? 'ok' : 'warn'} variant="soft">
          {p.stabilization}
        </Badge>
      )}
      {fund && <Badge tone="info" variant="outline">{fund.name}</Badge>}
      <HealthBadge health={p.health} />
    </>
  )

  const breadcrumb = (
    <>
      <Link to="/properties">Properties</Link>
      {owner && <> <span className="dim">/</span> <Link to={`/accounts/${owner.id}`}>{owner.name}</Link></>}
      <> <span className="dim">/</span> {p.name}</>
    </>
  )

  const actions = (
    <>
      {p.website && (
        <a href={p.website} target="_blank" rel="noreferrer">
          <Button variant="default" size="sm">Listing</Button>
        </a>
      )}
      {owner && (
        <Link to={`/accounts/${owner.id}`}>
          <Button variant="navy" size="sm">Owner Account</Button>
        </Link>
      )}
    </>
  )

  return (
    <div className="pd-wrap">
      <RecordHero
        tone="light"
        eyebrow={eyebrow}
        title={p.name}
        badges={badges}
        kpis={kpis}
        breadcrumb={breadcrumb}
        actions={actions}
      />

      {/* Email/Call/Meeting target the owner account's contacts; Note/Task land on this property. */}
      <QuickActionBar
        contacts={toActionContacts(p.ownerAccountId ? contactsForAccount(d, p.ownerAccountId) : [])}
        attachTo={{ kind: 'property', id: p.id, label: p.name }}
        contextLabel={p.name}
      />

      <div className="pd-tabbar">
        <Tabs tabs={tabs} active={tab} onChange={(t) => setTab(t as TabId)} />
      </div>

      {tab === 'overview' && <OverviewTab d={d} p={p} fp={fp} />}
      {tab === 'insights' && <InsightsTab d={d} p={p} fp={fp} />}
      {tab === 'financials' && <FinancialsTab p={p} fp={fp} />}
      {tab === 'vital' && <VitalFactorsTab p={p} fp={fp} />}
      {tab === 'workorders' && <WorkOrdersTab d={d} p={p} />}
      {tab === 'actions' && <ActionItemsTab d={d} p={p} />}
      {tab === 'history' && <HistoryTab d={d} p={p} />}
      {tab === 'files' && <FilesTab d={d} p={p} />}
    </div>
  )
}
